import { create } from 'zustand';

export interface MediaStates {
    camera_enabled: boolean;
    microphone_enabled: boolean;
}

export interface Meeting {
    id: string;
    title: string;
    status: string;
}

export interface MediaDeviceInfo {
    deviceId: string;
    label: string;
    kind: string;
}

interface MeetingState {
    // Participant and meeting data
    participantData: any | null;
    meeting: Meeting | null;

    // Media states
    currentMediaStates: MediaStates;
    alreadyJoined: boolean;

    // Permission states
    cameraPermissionGranted: boolean | null;
    micPermissionGranted: boolean | null;

    // Loading and error states
    loading: boolean;
    error: string;
    isJoining: boolean;

    // Audio device states
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
    selectedAudioInput: string;
    selectedAudioOutput: string;
    micLevel: number;
    isTestingSound: boolean;

    // Video settings states
    videoQuality: string;
    facingMode: "user" | "environment";
    isMirrored: boolean;
    videoInputDevices: MediaDeviceInfo[];

    // Tab state
    activeTab: "audio" | "video";

    // Actions
    setParticipantData: (data: any) => void;
    setMeeting: (meeting: Meeting | null) => void;
    setCurrentMediaStates: (states: MediaStates | ((prev: MediaStates) => MediaStates)) => void;
    setAlreadyJoined: (joined: boolean) => void;
    setCameraPermissionGranted: (granted: boolean | null) => void;
    setMicPermissionGranted: (granted: boolean | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string) => void;
    setIsJoining: (joining: boolean) => void;
    setAudioInputDevices: (devices: MediaDeviceInfo[]) => void;
    setAudioOutputDevices: (devices: MediaDeviceInfo[]) => void;
    setSelectedAudioInput: (deviceId: string) => void;
    setSelectedAudioOutput: (deviceId: string) => void;
    setMicLevel: (level: number) => void;
    setIsTestingSound: (testing: boolean) => void;
    setVideoQuality: (quality: string) => void;
    setFacingMode: (mode: "user" | "environment") => void;
    setIsMirrored: (mirrored: boolean) => void;
    setVideoInputDevices: (devices: MediaDeviceInfo[]) => void;
    setActiveTab: (tab: "audio" | "video") => void;
    resetMeetingState: () => void;
}

const initialState = {
    participantData: null,
    meeting: null,
    currentMediaStates: {
        camera_enabled: false,
        microphone_enabled: false,
    },
    alreadyJoined: false,
    cameraPermissionGranted: null,
    micPermissionGranted: null,
    loading: true,
    error: "",
    isJoining: false,
    audioInputDevices: [],
    audioOutputDevices: [],
    selectedAudioInput: "",
    selectedAudioOutput: "",
    micLevel: 0,
    isTestingSound: false,
    videoQuality: "auto",
    facingMode: "user" as const,
    isMirrored: true,
    videoInputDevices: [],
    activeTab: "video" as const,
};

export const useMeetingStore = create<MeetingState>()((set) => ({
    ...initialState,

    setParticipantData: (data) => set({ participantData: data }),
    setMeeting: (meeting) => set({ meeting }),
    setCurrentMediaStates: (states) =>
        set((state) => ({
            currentMediaStates: typeof states === 'function' ? states(state.currentMediaStates) : states,
        })),
    setAlreadyJoined: (joined) => set({ alreadyJoined: joined }),
    setCameraPermissionGranted: (granted) => set({ cameraPermissionGranted: granted }),
    setMicPermissionGranted: (granted) => set({ micPermissionGranted: granted }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setIsJoining: (joining) => set({ isJoining: joining }),
    setAudioInputDevices: (devices) => set({ audioInputDevices: devices }),
    setAudioOutputDevices: (devices) => set({ audioOutputDevices: devices }),
    setSelectedAudioInput: (deviceId) => set({ selectedAudioInput: deviceId }),
    setSelectedAudioOutput: (deviceId) => set({ selectedAudioOutput: deviceId }),
    setMicLevel: (level) => set({ micLevel: level }),
    setIsTestingSound: (testing) => set({ isTestingSound: testing }),
    setVideoQuality: (quality) => set({ videoQuality: quality }),
    setFacingMode: (mode) => set({ facingMode: mode }),
    setIsMirrored: (mirrored) => set({ isMirrored: mirrored }),
    setVideoInputDevices: (devices) => set({ videoInputDevices: devices }),
    setActiveTab: (tab) => set({ activeTab: tab }),
    resetMeetingState: () => set(initialState),
}));
